import { useMemo } from 'react';
import type { DistributionDataItem, RiskColors, SeverityLevel } from './types';

export type DistributionData = DistributionDataItem;

export const SEVERITY_COLORS: RiskColors = {
  critical: '#e22223',
  high: '#fc4028',
  medium: '#fe841c',
  low: '#62a228',
};

export const VULNERABILITY_COLORS: RiskColors = SEVERITY_COLORS;
export const LICENSE_COLORS: RiskColors = SEVERITY_COLORS;
export const OPERATIONAL_COLORS: RiskColors = SEVERITY_COLORS;
export const OVERALL_RISK_COLORS: RiskColors = SEVERITY_COLORS;

export const categorizeOverallRiskScore = (score: number): SeverityLevel => {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low'; 
};

export const categorizeVulnerabilityScore = (score: number): SeverityLevel => {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

export const categorizeLicenseScore = (score: number): SeverityLevel => {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

export const categorizeOperationalRiskScore = (score: number): SeverityLevel => {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

export const categorizeTechnicalDebtScore = (score: number): SeverityLevel => {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

export const scoreToCategory = (score: number): SeverityLevel => {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

export const getScoreCategoryOptions = () => [
  { label: 'Critical (90-100)', value: 'critical' },
  { label: 'High (70-89)', value: 'high' },
  { label: 'Medium (40-69)', value: 'medium' },
  { label: 'Low (0-39)', value: 'low' }
];


export const getRiskCategoryForTitle = (title: string, score: number): string => {
    if (title.includes('Overall Risk')) return categorizeOverallRiskScore(score);
    if (title.includes('Technical Debt')) return categorizeTechnicalDebtScore(score);
    if (title.includes('Operational')) return categorizeOperationalRiskScore(score);
    if (title.includes('Vulnerability')) return categorizeVulnerabilityScore(score);
    if (title.includes('License')) return categorizeLicenseScore(score);
    return 'low';
};

export const normalizeSeverityDistribution = (
  distribution: any, 
  colorMap: RiskColors
): DistributionData[] | null => {
  if (!distribution) {
    return null;
  }

  const severityOrder: SeverityLevel[] = ['critical', 'high', 'medium', 'low'];

  if (Array.isArray(distribution)) {
    return distribution.map((item: any) => {
      const rawSeverity = (item?.severity || item?.name || '').toString();
      const key = rawSeverity.toLowerCase();
      const name = rawSeverity ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase() : 'Unknown';
      const count = Number(item?.count ?? item?.value ?? item?.total ?? 0);

      return {
        name,
        count,
        color: colorMap[key] ?? '#6b7280',
      };
    });
  }

  if (typeof distribution === 'object') {
    return severityOrder.map((key) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      count: Number(distribution[key]) || 0,
      color: colorMap[key],
    }));
  }

  return null;
};



export const useOverallRiskDistribution = (data: any[] = []) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return [
        { name: 'Critical', count: 0, color: OVERALL_RISK_COLORS.critical },
        { name: 'High', count: 0, color: OVERALL_RISK_COLORS.high },
        { name: 'Medium', count: 0, color: OVERALL_RISK_COLORS.medium },
        { name: 'Low', count: 0, color: OVERALL_RISK_COLORS.low },
      ];
    }

    const toNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const overallBuckets = data.reduce((acc, item: any) => {
      // Handle both project format (avg_overall_score, latest_overall_score) and component format (overallscore)
      const score = toNumber(item.overallscore ?? item.avg_overall_score ?? item.latest_overall_score);
      const category = categorizeOverallRiskScore(score);
      acc[category] += 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });

    return [
      { name: 'Critical', count: overallBuckets.critical, color: OVERALL_RISK_COLORS.critical },
      { name: 'High', count: overallBuckets.high, color: OVERALL_RISK_COLORS.high },
      { name: 'Medium', count: overallBuckets.medium, color: OVERALL_RISK_COLORS.medium },
      { name: 'Low', count: overallBuckets.low, color: OVERALL_RISK_COLORS.low },
    ];
  }, [data]);
};

export const useVulnerabilityDistribution = (data: any[] = []) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return [
        { name: 'Critical', count: 0, color: VULNERABILITY_COLORS.critical },
        { name: 'High', count: 0, color: VULNERABILITY_COLORS.high },
        { name: 'Medium', count: 0, color: VULNERABILITY_COLORS.medium },
        { name: 'Low', count: 0, color: VULNERABILITY_COLORS.low },
      ];
    }

    const toNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const vulnerabilityBuckets = data.reduce((acc, item: any) => {
      // Handle both project format (avg_vulnerability_score, latest_vulnerability_score) and component format (vulnerabilityscore)
      const score = toNumber(item.vulnerabilityscore ?? item.avg_vulnerability_score ?? item.latest_vulnerability_score);
      const category = categorizeVulnerabilityScore(score);
      acc[category] += 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });

    return [
      { name: 'Critical', count: vulnerabilityBuckets.critical, color: VULNERABILITY_COLORS.critical },
      { name: 'High', count: vulnerabilityBuckets.high, color: VULNERABILITY_COLORS.high },
      { name: 'Medium', count: vulnerabilityBuckets.medium, color: VULNERABILITY_COLORS.medium },
      { name: 'Low', count: vulnerabilityBuckets.low, color: VULNERABILITY_COLORS.low },
    ];
  }, [data]);
};

export const useLicenseDistribution = (data: any[] = []) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return [
        { name: 'Critical', count: 0, color: LICENSE_COLORS.critical },
        { name: 'High', count: 0, color: LICENSE_COLORS.high },
        { name: 'Medium', count: 0, color: LICENSE_COLORS.medium },
        { name: 'Low', count: 0, color: LICENSE_COLORS.low },
      ];
    }
   const toNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const licenseBuckets = data.reduce((acc, item: any) => {
      // Handle both project format (avg_license_score, latest_license_score) and component format (licensescore)
      const score = toNumber(item.licensescore ?? item.avg_license_score ?? item.latest_license_score);
      const category = categorizeLicenseScore(score);
      acc[category] += 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });

    return [
      { name: 'Critical', count: licenseBuckets.critical, color: LICENSE_COLORS.critical },
      { name: 'High', count: licenseBuckets.high, color: LICENSE_COLORS.high },
      { name: 'Medium', count: licenseBuckets.medium, color: LICENSE_COLORS.medium },
      { name: 'Low', count: licenseBuckets.low, color: LICENSE_COLORS.low },
    ];
  }, [data]);
};

export const useOperationalRiskDistribution = (data: any[] = []) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return [
        { name: 'Critical', count: 0, color: OPERATIONAL_COLORS.critical },
        { name: 'High', count: 0, color: OPERATIONAL_COLORS.high },
        { name: 'Medium', count: 0, color: OPERATIONAL_COLORS.medium },
        { name: 'Low', count: 0, color: OPERATIONAL_COLORS.low },
      ];
    }

    const toNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const operationalBuckets = data.reduce((acc, item: any) => {
      // Handle both project format (avg_operational_risk_score, latest_operational_risk_score) and component format (operationalriskscore)
      const score = toNumber(item.operationalriskscore ?? item.avg_operational_risk_score ?? item.latest_operational_risk_score);
      const category = categorizeOperationalRiskScore(score);
      acc[category] += 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });

    return [
      { name: 'Critical', count: operationalBuckets.critical, color: OPERATIONAL_COLORS.critical },
      { name: 'High', count: operationalBuckets.high, color: OPERATIONAL_COLORS.high },
      { name: 'Medium', count: operationalBuckets.medium, color: OPERATIONAL_COLORS.medium },
      { name: 'Low', count: operationalBuckets.low, color: OPERATIONAL_COLORS.low },
    ];
  }, [data]);
};

export const useTechnicalDebtDistribution = (projects: any[] = []) => {
  return useMemo(() => {
    if (!projects || projects.length === 0) {
      return [
        { name: 'Critical', count: 0, color: SEVERITY_COLORS.critical },
        { name: 'High', count: 0, color: SEVERITY_COLORS.high },
        { name: 'Medium', count: 0, color: SEVERITY_COLORS.medium },
        { name: 'Low', count: 0, color: SEVERITY_COLORS.low },
      ];
    }

    const toNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const techDebtBuckets = projects.reduce((acc, project: any) => {
      const score = toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score);
      const category = categorizeTechnicalDebtScore(score);
      acc[category] += 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });

    return [
      { name: 'Critical', count: techDebtBuckets.critical, color: SEVERITY_COLORS.critical },
      { name: 'High', count: techDebtBuckets.high, color: SEVERITY_COLORS.high },
      { name: 'Medium', count: techDebtBuckets.medium, color: SEVERITY_COLORS.medium },
      { name: 'Low', count: techDebtBuckets.low, color: SEVERITY_COLORS.low },
    ];
  }, [projects]);
};